import React, { useEffect, useState } from "react";
import { api, errMsg, openSignedDoc } from "@/lib/api";
import { StatusPill, InlineLoader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import DocumentUpload from "@/components/DocumentUpload";
import { HeartHandshake, Loader2, AlertCircle, CheckCircle2, BadgeCheck, FileText } from "lucide-react";
import { toast } from "sonner";

export default function NonprofitVerify() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ org_name: "", ein: "", org_address: "", contact_email: "", attest: false });
  const [docs, setDocs] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/nonprofits/verification-status").then((r) => setStatus(r.data)).catch(() => setStatus({ status: "none" }));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.org_address.trim()) return setError("Please enter your organization's address.");
    if (!docs || docs.length === 0) return setError("Please upload at least one verification document (e.g., your IRS 501(c)(3) determination letter).");
    if (!form.attest) return setError("Please confirm the attestation.");
    setLoading(true);
    try {
      await api.post("/nonprofits/verify", {
        org_name: form.org_name, ein: form.ein, org_address: form.org_address, contact_email: form.contact_email,
        documents: docs,
      });
      toast.success("Verification submitted for review.");
      await load();
    } catch (err) { setError(errMsg(err)); }
    finally { setLoading(false); }
  };

  if (!status) return <InlineLoader label="Loading" />;

  const isApproved = status.status === "approved";
  const isPending = ["pending", "needs_info"].includes(status.status);

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-1 flex items-center gap-2 text-ff-green"><HeartHandshake className="h-5 w-5" /><span className="text-xs font-bold uppercase tracking-wider">Nonprofit verification</span></div>
      <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ff-blue sm:text-3xl">Get the Nonprofit Verified badge</h1>
      <p className="mt-1 text-sm text-ff-medium">U.S. 501(c)(3) organizations only. Verification is required before the badge appears.</p>

      {isApproved && (
        <Alert className="mt-6 border-ff-green/30 bg-[#ECFDF3]" data-testid="np-approved">
          <BadgeCheck className="h-4 w-4 text-ff-green" />
          <AlertDescription className="text-ff-dark">Your organization <b>{status.org_name}</b> is verified. The Nonprofit Verified badge appears on your campaigns.</AlertDescription>
        </Alert>
      )}
      {isPending && (
        <Alert className="mt-6 border-ff-amber/30 bg-[#FFF7ED]" data-testid="np-pending">
          <Loader2 className="h-4 w-4 text-ff-amber" />
          <AlertDescription className="text-ff-dark">
            Your verification is <StatusPill status={status.status} />. {status.review_notes ? `Notes: ${status.review_notes}` : "Our team is reviewing your submission."}
          </AlertDescription>
        </Alert>
      )}

      {(isApproved || isPending) && status.documents?.length > 0 && (
        <div className="mt-4 rounded-xl border border-ff-border bg-white p-4" data-testid="np-submitted-docs">
          <div className="mb-2 text-xs font-bold uppercase tracking-wider text-ff-medium">Submitted documents</div>
          <ul className="space-y-2">
            {status.documents.map((doc, i) => (
              <li key={i}>
                <button type="button" onClick={() => openSignedDoc(doc).catch((e) => toast.error(errMsg(e)))}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-ff-blue hover:underline">
                  <FileText className="h-4 w-4 text-ff-medium" /> {doc.name || `Document ${i + 1}`}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!isApproved && (
        <form onSubmit={submit} className="mt-6 rounded-2xl border border-ff-border bg-white p-8 shadow-sm" data-testid="np-verify-form">
          {error && <Alert variant="destructive" className="mb-4"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
          <div className="space-y-4">
            <div><Label>Organization legal name <span className="text-ff-red">*</span></Label><Input required data-testid="np-org-name" value={form.org_name} onChange={set("org_name")} className="mt-1.5" /></div>
            <div><Label>EIN <span className="text-ff-red">*</span></Label><Input required data-testid="np-ein" value={form.ein} onChange={set("ein")} className="mt-1.5" placeholder="45-1234567" /></div>
            <div><Label>Organization address <span className="text-ff-red">*</span></Label><Textarea rows={2} data-testid="np-address" value={form.org_address} onChange={set("org_address")} className="mt-1.5" /></div>
            <div><Label>Contact email <span className="text-ff-red">*</span></Label><Input type="email" required data-testid="np-contact-email" value={form.contact_email} onChange={set("contact_email")} className="mt-1.5" /></div>
            <div>
              <Label>Verification documents <span className="text-ff-red">*</span></Label>
              <p className="mb-2 mt-0.5 text-xs text-ff-medium">Upload your IRS 501(c)(3) determination letter, EIN letter, or other proof. PDF or images.</p>
              <DocumentUpload value={docs} onChange={setDocs} testid="np-docs" />
            </div>
            <label className="flex cursor-pointer items-start gap-3 text-sm text-ff-dark">
              <Checkbox data-testid="np-attest" checked={form.attest} onCheckedChange={(v) => setForm({ ...form, attest: !!v })} />
              I attest this organization is a registered U.S. nonprofit and the information provided is accurate.
            </label>
          </div>
          <Button type="submit" disabled={loading} data-testid="np-submit" className="mt-6 w-full bg-ff-green font-semibold text-white hover:bg-ff-green/90">
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting</> : "Submit for verification"}
          </Button>
        </form>
      )}
    </div>
  );
}
